import memoizeOne from 'react-utils/memoizeOne';
import { isEqual } from 'underscore';
import { getDeepValue, mergeDeep } from 'ContentUtils/helpers/ObjectHelpers';
import { getFieldFromKey } from '../utils';
import { FONT_SETS } from 'ContentUtils/constants/Fonts';
import { FONT_FIELD_KEYS, CSS_VAR_STYLE_EL_ID, PREVIEW_CLASSNAME, FONTS_BASE_KEY } from '../../../constants/GlobalStyles';
export const getLoadableFonts = (fontFields, fontOverrides) => {
  const insertedFonts = {};
  return Object.keys(FONT_FIELD_KEYS).reduce((acc, key) => {
    const fieldKey = FONT_FIELD_KEYS[key];
    let value = getDeepValue(fontOverrides, fieldKey);
    if (value === undefined) {
      const field = getFieldFromKey(fontFields, fieldKey);
      value = field === null || field === void 0 ? void 0 : field.default;
    }
    const {
      font: fontFamily,
      font_set: fontSet
    } = value || {};
    if (fontFamily && !insertedFonts[fontFamily] && (fontSet === FONT_SETS.BRANDING || fontSet === FONT_SETS.GOOGLE)) {
      insertedFonts[fontFamily] = true;
      acc.push(value);
    }
    return acc;
  }, []);
};
const BASE_FONT_FIELD_KEYS = Object.keys(FONT_FIELD_KEYS).reduce((acc, key) => {
  const partialFieldKey = FONT_FIELD_KEYS[key];
  const joinedFieldKey = FONTS_BASE_KEY.concat(partialFieldKey).join('.');
  return Object.assign(acc, {
    [joinedFieldKey]: joinedFieldKey
  });
}, {});
export const getBaseFontWasChanged = (fields, oldGlobalStyles, updatedFieldKey, newValue) => {
  const isBaseFontUpdate = BASE_FONT_FIELD_KEYS[updatedFieldKey.join('.')];
  if (isBaseFontUpdate) {
    var _oldValue;
    const field = getFieldFromKey(fields, updatedFieldKey);
    let oldValue = getDeepValue(oldGlobalStyles, updatedFieldKey);
    oldValue = oldValue !== undefined ? oldValue : field === null || field === void 0 ? void 0 : field.default;
    return Boolean(newValue.font && ((_oldValue = oldValue) === null || _oldValue === void 0 ? void 0 : _oldValue.font) !== newValue.font);
  }
  return false;
};

// TEMPORARY - CSS vars should be fetched via API
const getCssVarMap = (fields, globalStyles = {}, cssVarMap = {}, fieldKeyPrefix = []) => {
  return fields.reduce((acc, field) => {
    let value = globalStyles[field.name];
    const cssVarName = field.css_variable;
    if (value === undefined) {
      value = field.default;
    } else if (field.default && typeof field.default === 'object' && typeof value === 'object') {
      value = Object.assign({}, field.default, value);
    }
    if (field.type === 'group' && field.children) {
      return Object.assign(acc, getCssVarMap(field.children, value, acc, fieldKeyPrefix.concat([field.name])));
    }
    if (field.type === 'color' && value) {
      acc[`--${cssVarName}`] = value.color;
    } else if (field.type === 'font' && value) {
      acc[`--${cssVarName}-font`] = value.font;
      acc[`--${cssVarName}-size`] = value.size;
    } else {
      acc[`--${cssVarName}`] = value;
    }
    return acc;
  }, cssVarMap);
};
const getCssVarClass = (fields, globalStyles = {}) => {
  const cssVarMap = getCssVarMap(fields, globalStyles);
  const cssVarMapAsString = Object.keys(cssVarMap).reduce((acc, cssVarName) => {
    return `${acc}\n${cssVarName}: ${cssVarMap[cssVarName]};`;
  }, '');
  return `.${PREVIEW_CLASSNAME} { ${cssVarMapAsString} }`;
};
const memoizedGetCssVarClass = memoizeOne(getCssVarClass, isEqual);
export const loadCssVarMap = (fields, globalStyles = {}) => {
  const cssVarString = memoizedGetCssVarClass(fields, globalStyles);
  const cssVarStyleEl = document.querySelector(`[id="${CSS_VAR_STYLE_EL_ID}"]`);
  if (cssVarStyleEl) {
    cssVarStyleEl.textContent = cssVarString;
  } else {
    const styleEl = document.createElement('style');
    styleEl.setAttribute('id', CSS_VAR_STYLE_EL_ID);
    styleEl.textContent = cssVarString;
    const head = document.querySelector('head');
    head === null || head === void 0 || head.appendChild(styleEl);
  }
};

// TEMPORARY - Variant CSS classes should be fetched via API
const getVariantCssVarClass = (variantDefinition, variantDefinitionOverrides) => {
  const {
    fields,
    defaultVariantsConfig
  } = variantDefinition;
  return Object.keys(defaultVariantsConfig).reduce((acc, variantName) => {
    const {
      values
    } = defaultVariantsConfig[variantName] || {};
    const cssVarMap = getCssVarMap(fields, mergeDeep(values || {}, variantDefinitionOverrides[variantName] || {}));
    const cssVarMapAsString = Object.keys(cssVarMap).reduce((_acc, cssVarName) => {
      return `${_acc}\n${cssVarName}: ${cssVarMap[cssVarName]};`;
    }, '');
    return `${acc}\n.${PREVIEW_CLASSNAME} [data-variant-name="${variantName}"] {\n${cssVarMapAsString}\n}`;
  }, '');
};
const memoizedGetVariantCssVarClass = memoizeOne(getVariantCssVarClass, isEqual);
export const loadVariantCssVarMap = (variantDefinition, variantDefinitionOverrides) => {
  const {
    name
  } = variantDefinition;
  const variantCssVarString = memoizedGetVariantCssVarClass(variantDefinition, variantDefinitionOverrides);
  const variantCssVarStyleEl = document.querySelector(`[data-variant-definition-name="${name}"]`);
  if (variantCssVarStyleEl) {
    variantCssVarStyleEl.textContent = variantCssVarString;
  } else {
    const styleEl = document.createElement('style');
    styleEl.setAttribute('data-variant-definition-name', name);
    styleEl.textContent = variantCssVarString;
    const head = document.querySelector('head');
    head === null || head === void 0 || head.appendChild(styleEl);
  }
};
export const removeAllGlobalStyles = () => {
  const cssVarStyleEl = document.querySelector(`[id="${CSS_VAR_STYLE_EL_ID}"]`);
  cssVarStyleEl === null || cssVarStyleEl === void 0 || cssVarStyleEl.remove();
  const fontStyleEls = document.querySelectorAll('[data-font-family]');
  fontStyleEls === null || fontStyleEls === void 0 || fontStyleEls.forEach(fontStyleEl => fontStyleEl === null || fontStyleEl === void 0 ? void 0 : fontStyleEl.remove());
};